import type { CoachWeeklyAvailability, CoachTimeBlock, CoachingSession } from '@/types/coaching.types';

export interface Slot {
  start: string; // "HH:MM"
  end: string;   // "HH:MM"
  available: boolean;
  reason?: 'taken' | 'blocked' | 'too_soon' | 'too_short';
}

/**
 * Expand a recurring time block into concrete instances that overlap the target date.
 * Extracted from sessions.service.ts to be shared with client-side slot computation.
 */
export function expandRecurringBlock(
  block: CoachTimeBlock,
  targetDate: Date
): { start: Date; end: Date }[] {
  const blockStart = new Date(block.start_at);
  const blockEnd = new Date(block.end_at);
  const durationMs = blockEnd.getTime() - blockStart.getTime();

  if (!block.recurrence) {
    return [{ start: blockStart, end: blockEnd }];
  }

  const capStart = new Date(targetDate.getTime() - 90 * 24 * 60 * 60 * 1000);
  const capEnd = new Date(targetDate.getTime() + 90 * 24 * 60 * 60 * 1000);
  const hardEnd = block.recurrence_end_at
    ? new Date(Math.min(new Date(block.recurrence_end_at).getTime(), capEnd.getTime()))
    : capEnd;

  const stepMs: Record<string, number> = {
    weekly: 7 * 24 * 60 * 60 * 1000,
    biweekly: 14 * 24 * 60 * 60 * 1000,
    monthly: 30 * 24 * 60 * 60 * 1000,
  };
  const step = stepMs[block.recurrence];
  if (!step) return [{ start: blockStart, end: blockEnd }];

  const instances: { start: Date; end: Date }[] = [];
  let cursor = blockStart.getTime();
  while (cursor <= hardEnd.getTime()) {
    if (cursor >= capStart.getTime()) {
      instances.push({ start: new Date(cursor), end: new Date(cursor + durationMs) });
    }
    cursor += step;
  }
  return instances;
}

function timeToMinutes(hhmm: string): number {
  const [h, m] = hhmm.split(':').map(Number);
  return h * 60 + m;
}

function minutesToTime(mins: number): string {
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/**
 * Compute available 30-min slots for a coach on a specific date.
 * Pure function — no async, no side effects. Testable in isolation.
 */
export function computeAvailableSlots(
  weeklyAvailability: CoachWeeklyAvailability[],
  timeBlocks: CoachTimeBlock[],
  sessions: CoachingSession[],
  date: Date,
  durationMins: number,
  policies: { minNoticeHours: number }
): Slot[] {
  const dow = date.getDay(); // 0=Sun..6=Sat

  // If we have availability rows, find this day's row
  const availRow = weeklyAvailability.find(r => r.day_of_week === dow);

  // If coach has set up availability and this day is off → no slots
  if (weeklyAvailability.length > 0 && availRow && !availRow.is_available) {
    return [];
  }

  // Default working hours when no availability rows configured
  const startMins = availRow?.start_time ? timeToMinutes(availRow.start_time) : timeToMinutes('09:00');
  const endMins   = availRow?.end_time   ? timeToMinutes(availRow.end_time)   : timeToMinutes('18:00');

  if (endMins <= startMins) return [];

  // Build the date prefix for comparing with local-day times
  const year  = date.getFullYear();
  const month = date.getMonth();
  const day   = date.getDate();

  // Earliest bookable moment
  const noticeDeadline = new Date(Date.now() + policies.minNoticeHours * 60 * 60 * 1000);

  // Expand all time blocks that touch this date once
  const blockInstances: { start: Date; end: Date }[] = [];
  for (const block of timeBlocks) {
    const expanded = expandRecurringBlock(block, date);
    for (const inst of expanded) {
      // Keep only instances that overlap this calendar day
      const dayStart = new Date(year, month, day, 0, 0, 0);
      const dayEnd   = new Date(year, month, day, 23, 59, 59);
      if (inst.start <= dayEnd && inst.end >= dayStart) {
        blockInstances.push(inst);
      }
    }
  }

  const slots: Slot[] = [];

  // Generate every 30-min slot from startMins to (endMins - 30)
  for (let slotStart = startMins; slotStart + 30 <= endMins; slotStart += 30) {
    const slotEnd = slotStart + 30;
    const slotStartDate = new Date(year, month, day, Math.floor(slotStart / 60), slotStart % 60, 0);
    const slotEndDate   = new Date(year, month, day, Math.floor(slotEnd   / 60), slotEnd   % 60, 0);

    let available = true;
    let reason: Slot['reason'];

    // Check booked sessions first — "Taken" takes priority over "Too soon"
    for (const s of sessions) {
      if (!['scheduled', 'confirmed'].includes(s.status)) continue;
      const sStart = new Date(s.scheduled_at);
      const sEnd   = new Date(sStart.getTime() + (s.duration_mins || 60) * 60 * 1000);
      if (sEnd.getTime() <= Date.now()) continue; // stale session
      if (sStart < slotEndDate && sEnd > slotStartDate) {
        available = false;
        reason = 'taken';
        break;
      }
    }

    // Time block overlap (blocked > too_soon)
    if (available) {
      for (const inst of blockInstances) {
        if (inst.start < slotEndDate && inst.end > slotStartDate) {
          available = false;
          reason = 'blocked';
          break;
        }
      }
    }

    // Notice window check
    if (available && slotStartDate < noticeDeadline) {
      available = false;
      reason = 'too_soon';
    }

    // Duration fit check: does chosen duration fit before end of working hours?
    if (available && slotStart + durationMins > endMins) {
      available = false;
      reason = 'too_short';
    }

    slots.push({
      start: minutesToTime(slotStart),
      end: minutesToTime(slotEnd),
      available,
      ...(reason ? { reason } : {}),
    });
  }

  return slots;
}

/**
 * Check whether a given day has at least one available slot fitting durationMins.
 * Used to colour calendar days.
 */
export function dayHasAvailability(
  weeklyAvailability: CoachWeeklyAvailability[],
  timeBlocks: CoachTimeBlock[],
  sessions: CoachingSession[],
  date: Date,
  durationMins: number,
  policies: { minNoticeHours: number }
): boolean {
  const slots = computeAvailableSlots(weeklyAvailability, timeBlocks, sessions, date, durationMins, policies);
  return slots.some(s => s.available);
}
